import React from 'react'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import i18n from 'config/i18n'

import { SubmissionState, SUBMISSION_VIEW_ENUM, PartiposterProfile, SocialMediaAccount } from 'state/submissions'
import { TaskState, EngagementId, LINK_IN_BIO_TYPE_ENUM } from 'state/tasks'
import { parseBackendDate } from 'services/dates'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { UrlCell, UrlCellProps } from 'components/Shared/MuiTable/Cell/UrlCell'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { CommentCell } from 'components/Shared/MuiTable/Cell/CommentCell'
import { MediaCell, MediaCellProps } from 'components/Shared/MuiTable/Cell/MediaCell'
import { getInfoTooltipHeaderCell } from 'components/Shared/MuiTable/Cell/InfoTooltipHeaderCell'
import { Country, PLATFORM_ENUM } from 'services/api/constants'
import { TagContentType } from 'particles/Tag'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { GridColDefProps } from '../SimpleTable/constants'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { convertSecondsToTime } from 'components/utils'

export interface RowsProps {
    // Note: Row id is treated from MUI DataGridPro component as list key, don't use index for dynamic list key
    // issue related: https://gitlab.com/partipost-dev/engineering/backlog-items/-/issues/246
    id: string
    uuid: string
    socialMediaInfo: {
        profiles: SocialMediaAccount[]
    }
    insights_screenshot_files: string[]
    view_count_screenshot_files: string[]
    follower_count: {
        value: string
        color?: string
    }
    engagement_impression_count?: {
        value: string
        color?: string
    }
    engagement_like_count?: {
        value: string
        color?: string
    }
    engagement_reach_count?: {
        value: string
        color?: string
    }
    engagement_sticker_tap_count?: {
        value: string
        color?: string
    }
    engagement_view_count?: {
        value: string
        color?: string
    }
    engagement_link_click_count?: {
        value: string
        color?: string
    }
    engagement_total_watch_time_second_count?: {
        value: string
        color?: string
    }
    engagement_total_play_time_second_count?: {
        value: string
        color?: string
    }
    engagement_watched_full_video_percentage_count?: {
        value: string
        color?: string
    }
    engagement_average_watch_time_second_count?: {
        value: string
        color?: string
    }
    postingTime: {
        value: string
        color?: string
    }
    linkInBioUrl: string
    lastReviewedBy: {
        value?: string
    }
    lastReviewedAt: {
        value?: string
    }
    editsRequested: {
        submission?: SubmissionState
        contentType: TagContentType
    }
    partiposterTags: {
        profileData: PartiposterProfile
        submissionUuid: string
        campaignUuid: string
    }
}

export const getColumns = (
    viewType: SUBMISSION_VIEW_ENUM | null,
    showLinkInBio: boolean,
    engagementTypes: EngagementId[],
    canManageTag: boolean,
): GridColDefProps[] => {
    if (viewType === 'edits_requested') {
        return [
            {
                field: 'profile',
                type: 'string',
                headerName: i18n.t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: i18n.t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: i18n.t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: i18n.t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: i18n.t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    {
                        field: FIELDS_TYPE.IG_USERNAME,
                        headerName: i18n.t('submission.table.instagramUsernameLabel'),
                    },
                ],
            },
            {
                field: 'view_count_screenshot_files',
                headerName: i18n.t('submission.table.storyScreenshotLabel'),
                width: 175,
                renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'insights_screenshot_files',
                headerName: i18n.t('submission.table.engagementScreenshotLabel'),
                width: 220,
                renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'follower_count',
                headerName: i18n.t('submission.table.followerCountLabel'),
                width: 240,
                sortable: false,
                filterable: false,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                renderHeader: getInfoTooltipHeaderCell({
                    width: '190px',
                    title: i18n.t('submission.table.followerCountLabel'),
                    description: i18n.t(
                        'submission.table.followerCountTooltip',
                        'The number Partiposters entered when submitting engagement result for the campaign',
                    ),
                }),
            },
            ...(engagementTypes.includes(EngagementId.VIEW)
                ? [
                      {
                          field: 'engagement_view_count',
                          headerName: i18n.t('submission.table.viewsLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.LIKE)
                ? [
                      {
                          field: 'engagement_like_count',
                          headerName: i18n.t('submission.table.likesLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.STICKER_TAP)
                ? [
                      {
                          field: 'engagement_sticker_tap_count',
                          headerName: i18n.t('submission.table.stickerTapsLabel'),
                          width: 125,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.REACH)
                ? [
                      {
                          field: 'engagement_reach_count',
                          headerName: i18n.t('submission.table.reachLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.IMPRESSION)
                ? [
                      {
                          field: 'engagement_impression_count',
                          headerName: i18n.t('submission.table.impressionLabel'),
                          width: 125,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.LINK_CLICK)
                ? [
                      {
                          field: 'engagement_link_click_count',
                          headerName: i18n.t('submission.table.linkClicksLabel'),
                          width: 125,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.TOTAL_WATCH_TIME)
                ? [
                      {
                          field: 'engagement_total_watch_time_second_count',
                          headerName: i18n.t('engagementMetric.totalWatchTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.TOTAL_PLAY_TIME)
                ? [
                      {
                          field: 'engagement_total_play_time_second_count',
                          headerName: i18n.t('submission.campaignReport.metric.totalPlayTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.WATCHED_FULL_VIDEO)
                ? [
                      {
                          field: 'engagement_watched_full_video_percentage_count',
                          headerName: i18n.t('engagementMetric.watchedFullVideo'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.AVERAGE_WATCH_TIME)
                ? [
                      {
                          field: 'engagement_average_watch_time_second_count',
                          headerName: i18n.t('engagementMetric.averageWatchTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(showLinkInBio
                ? [
                      {
                          field: 'linkInBioUrl',
                          type: 'string',
                          headerName: i18n.t('submission.table.linkInBioLabel'),
                          width: 170,
                          renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            {
                field: 'lastReviewedBy',
                type: 'string',
                headerName: i18n.t('submission.table.lastReviewedLabel'),
                minWidth: 180,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'lastReviewedAt',
                type: 'string',
                headerName: i18n.t('submission.table.lastReviewedDateLabel'),
                minWidth: 180,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'editsRequested',
                type: 'string',
                headerName: i18n.t('submission.table.reasonEditRequestLabel'),
                width: 200,
                renderCell: (props: GridRenderCellParams) => <CommentCell {...props} />,
                sortable: false,
                filterable: false,
            },
            ...(canManageTag
                ? [
                      {
                          field: 'partiposterTags',
                          headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                          width: 240,
                          minWidth: 200,
                          renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                          sortable: false,
                          filterable: true,
                      },
                  ]
                : []),
        ]
    } else {
        return [
            {
                field: 'profile',
                type: 'string',
                headerName: i18n.t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: i18n.t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: i18n.t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: i18n.t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: i18n.t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    {
                        field: FIELDS_TYPE.IG_USERNAME,
                        headerName: i18n.t('submission.table.instagramUsernameLabel'),
                    },
                    {
                        field: FIELDS_TYPE.FB_USERNAME,
                        headerName: i18n.t('submission.table.facebookUsernameLabel'),
                    },
                    {
                        field: FIELDS_TYPE.TIKTOK_USERNAME,
                        headerName: i18n.t('submission.table.tiktokUsernameLabel'),
                    },
                    {
                        field: FIELDS_TYPE.TWITTER_USERNAME,
                        headerName: i18n.t('submission.table.twitterUsernameLabel'),
                    },
                    {
                        field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                        headerName: i18n.t('submission.table.XHSUsernameLabel'),
                    },
                ],
            },
            {
                field: 'postingTime',
                type: 'dateTime',
                headerName: i18n.t('submission.table.publishedTimeLabel'),
                width: 150,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'view_count_screenshot_files',
                headerName: i18n.t('submission.table.storyScreenshotLabel'),
                width: 175,
                renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'insights_screenshot_files',
                headerName: i18n.t('submission.table.engagementScreenshotLabel'),
                width: 220,
                renderCell: (props: MediaCellProps) => <MediaCell {...props} />,
                sortable: false,
                filterable: false,
            },
            {
                field: 'follower_count',
                headerName: i18n.t('submission.table.followerCountLabel'),
                width: 240,
                sortable: false,
                filterable: false,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                renderHeader: getInfoTooltipHeaderCell({
                    width: '190px',
                    title: i18n.t('submission.table.followerCountLabel'),
                    description: i18n.t(
                        'submission.table.followerCountTooltip',
                        'The number Partiposters entered when submitting engagement result for the campaign',
                    ),
                }),
            },
            ...(engagementTypes.includes(EngagementId.VIEW)
                ? [
                      {
                          field: 'engagement_view_count',
                          headerName: i18n.t('submission.table.viewsLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.LIKE)
                ? [
                      {
                          field: 'engagement_like_count',
                          headerName: i18n.t('submission.table.likesLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.STICKER_TAP)
                ? [
                      {
                          field: 'engagement_sticker_tap_count',
                          headerName: i18n.t('submission.table.stickerTapsLabel'),
                          width: 125,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.REACH)
                ? [
                      {
                          field: 'engagement_reach_count',
                          headerName: i18n.t('submission.table.reachLabel'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.IMPRESSION)
                ? [
                      {
                          field: 'engagement_impression_count',
                          headerName: i18n.t('submission.table.impressionLabel'),
                          width: 125,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.LINK_CLICK)
                ? [
                      {
                          field: 'engagement_link_click_count',
                          headerName: i18n.t('submission.table.linkClicksLabel'),
                          width: 125,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.TOTAL_WATCH_TIME)
                ? [
                      {
                          field: 'engagement_total_watch_time_second_count',
                          headerName: i18n.t('engagementMetric.totalWatchTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.TOTAL_PLAY_TIME)
                ? [
                      {
                          field: 'engagement_total_play_time_second_count',
                          headerName: i18n.t('submission.campaignReport.metric.totalPlayTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.WATCHED_FULL_VIDEO)
                ? [
                      {
                          field: 'engagement_watched_full_video_percentage_count',
                          headerName: i18n.t('engagementMetric.watchedFullVideo'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(engagementTypes.includes(EngagementId.AVERAGE_WATCH_TIME)
                ? [
                      {
                          field: 'engagement_average_watch_time_second_count',
                          headerName: i18n.t('engagementMetric.averageWatchTime'),
                          width: 105,
                          sortable: false,
                          filterable: false,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                      },
                  ]
                : []),
            ...(showLinkInBio
                ? [
                      {
                          field: 'linkInBioUrl',
                          type: 'string',
                          headerName: i18n.t('submission.table.linkInBioLabel'),
                          width: 200,
                          renderCell: (props: UrlCellProps) => <UrlCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(viewType !== SUBMISSION_VIEW_ENUM.PENDING
                ? [
                      {
                          field: 'lastReviewedBy',
                          type: 'string',
                          headerName: i18n.t('submission.table.lastReviewedLabel'),
                          minWidth: 180,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                      {
                          field: 'lastReviewedAt',
                          type: 'string',
                          headerName: i18n.t('submission.table.lastReviewedDateLabel'),
                          minWidth: 180,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(canManageTag
                ? [
                      {
                          field: 'partiposterTags',
                          headerName: i18n.t('submission.table.partiposterTagsLabel', 'Partiposter Tags'),
                          width: 240,
                          minWidth: 160,
                          renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
        ]
    }
}

export const getRows = (
    viewType: SUBMISSION_VIEW_ENUM | null,
    campaignUuid: string,
    submissions: SubmissionState[],
    profileById: Record<string, PartiposterProfile>,
    task: TaskState,
    country: Country,
): RowsProps[] => {
    return submissions.map(submission => {
        const profile = profileById[submission.partiposterId]
        const { social_media_accounts: socialMediaAccounts } = profile
        const igProfile = socialMediaAccounts.filter(account => account.platform === PLATFORM_ENUM.IG)[0]
        const fbProfile = socialMediaAccounts.filter(account => account.platform === PLATFORM_ENUM.FB)[0]
        const tiktokProfile = socialMediaAccounts.filter(account => account.platform === PLATFORM_ENUM.TIKTOK)[0]
        const twitterProfile = socialMediaAccounts.filter(account => account.platform === PLATFORM_ENUM.TWITTER)[0]
        const xiaohongshuProfile = socialMediaAccounts.filter(
            account => account.platform === PLATFORM_ENUM.XIAOHONGSHU,
        )[0]
        let linkInBioUrl
        if (task && task.data) {
            if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK) {
                linkInBioUrl = task.data['link_in_bio_url']
            } else if (task.data.link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK) {
                if (submission.submission_task != undefined) {
                    linkInBioUrl = submission.submission_task.data['link_in_bio_url']
                }
            }
        }
        return {
            id: submission.uuid,
            uuid: submission.uuid,
            profile: {
                profile,
            },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile, xiaohongshuProfile],
            },
            insights_screenshot_files:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? submission && submission.data && submission.data.insights_screenshot_files
                    : submission && submission.reviewed_data && submission.reviewed_data.insights_screenshot_files,
            view_count_screenshot_files:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? submission && submission.data && submission.data.view_count_screenshot_files
                    : submission && submission.reviewed_data && submission.reviewed_data.view_count_screenshot_files,
            postingTime:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: '' }
                    : submission.reviewed_data
                      ? { value: parseBackendDate(submission.reviewed_data.posting_time, country) }
                      : { value: '' },
            follower_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.follower_count }
                    : { value: submission && submission.reviewed_data && submission.reviewed_data.follower_count },
            engagement_impression_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_impression_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_impression_count,
                      },
            engagement_like_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_like_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_like_count,
                      },
            engagement_reach_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_reach_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_reach_count,
                      },
            engagement_sticker_tap_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_sticker_tap_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_sticker_tap_count,
                      },
            engagement_view_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_view_count }
                    : {
                          value:
                              submission && submission.reviewed_data && submission.reviewed_data.engagement_view_count,
                      },
            engagement_link_click_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { value: submission && submission.data && submission.data.engagement_link_click_count }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              submission.reviewed_data.engagement_link_click_count,
                      },
            engagement_total_watch_time_second_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? {
                          value:
                              submission &&
                              submission.data &&
                              convertSecondsToTime(submission.data.engagement_total_watch_time_second_count),
                      }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              convertSecondsToTime(submission.reviewed_data.engagement_total_watch_time_second_count),
                      },
            engagement_total_play_time_second_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? {
                          value:
                              submission &&
                              submission.data &&
                              convertSecondsToTime(submission.data.engagement_total_play_time_second_count),
                      }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              convertSecondsToTime(submission.reviewed_data.engagement_total_play_time_second_count),
                      },
            engagement_watched_full_video_percentage_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? {
                          value:
                              submission &&
                              submission.data &&
                              `${submission.data.engagement_watched_full_video_percentage_count}%`,
                      }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              `${submission.reviewed_data.engagement_watched_full_video_percentage_count}%`,
                      },
            engagement_average_watch_time_second_count:
                viewType === SUBMISSION_VIEW_ENUM.PENDING || viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? {
                          value:
                              submission &&
                              submission.data &&
                              convertSecondsToTime(submission.data.engagement_average_watch_time_second_count),
                      }
                    : {
                          value:
                              submission &&
                              submission.reviewed_data &&
                              convertSecondsToTime(submission.reviewed_data.engagement_average_watch_time_second_count),
                      },
            linkInBioUrl: linkInBioUrl,
            lastReviewedBy: {
                value: submission.reviewed_by_system
                    ? i18n.t('statusLabel.autoApproved')
                    : submission.moved_out_of_pending_by?.name,
            },
            lastReviewedAt: {
                value: submission.moved_out_of_pending_at
                    ? parseBackendDate(submission.moved_out_of_pending_at, country, { showTime: true })
                    : undefined,
            },
            editsRequested:
                viewType === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
                    ? { submission: submission, contentType: 'warning' }
                    : { submission: undefined, contentType: 'warning' },
            partiposterTags: {
                profileData: profile,
                submissionUuid: submission.submission_uuid,
                campaignUuid: campaignUuid,
            },
        }
    })
}
